import { LitElement, html, css } from 'lit';

export class SettingsMode extends LitElement {
  static styles = css`
    :host {
      display: block;
    }

    .settings-container {
      background: white;
      border-radius: 20px;
      padding: 24px;
      box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
    }

    .section-title {
      font-size: 1.5rem;
      font-weight: 700;
      color: #333;
      margin-bottom: 24px;
      text-align: center;
    }

    .settings-section {
      margin-bottom: 28px;
    }

    .section-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 12px;
    }

    .section-label {
      font-size: 1rem;
      font-weight: 600;
      color: #444;
    }

    .select-all-btn {
      font-size: 0.75rem;
      padding: 8px 14px;
      background: white;
      color: #667eea;
      border: 2px solid #e8e8e8;
      border-radius: 20px;
      cursor: pointer;
      font-weight: 600;
      transition: all 0.2s ease;
    }

    .select-all-btn:hover {
      border-color: #667eea;
      background: #f8f9ff;
    }

    .chapters-grid {
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      gap: 12px;
    }

    .chapter-btn {
      padding: 16px 8px;
      border: 2px solid #e8e8e8;
      border-radius: 14px;
      background: white;
      cursor: pointer;
      transition: all 0.2s ease;
      font-weight: 600;
      font-size: 0.95rem;
      color: #555;
    }

    .chapter-btn:hover {
      border-color: #667eea;
      background: #f8f9ff;
      transform: translateY(-2px);
    }

    .chapter-btn.selected {
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      color: white;
      border-color: transparent;
      box-shadow: 0 4px 16px rgba(102, 126, 234, 0.35);
    }

    .pages-section {
      background: linear-gradient(135deg, #f8f9ff 0%, #f0f2ff 100%);
      border-radius: 16px;
      padding: 16px;
      border: 1px solid rgba(102, 126, 234, 0.1);
    }

    .pages-grid {
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      gap: 10px;
    }

    .page-btn {
      padding: 12px 6px;
      border: 2px solid #e8e8e8;
      border-radius: 10px;
      background: white;
      cursor: pointer;
      transition: all 0.2s ease;
      font-size: 0.8rem;
      font-weight: 500;
      color: #555;
    }

    .page-btn:hover {
      border-color: #667eea;
      background: #f8f9ff;
      transform: translateY(-1px);
    }

    .page-btn.selected {
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      color: white;
      border-color: transparent;
      box-shadow: 0 2px 8px rgba(102, 126, 234, 0.3);
    }

    .no-chapter-selected {
      text-align: center;
      color: #888;
      padding: 32px 20px;
      background: #f8f9fa;
      border-radius: 16px;
      font-size: 0.95rem;
    }

    .save-btn {
      width: 100%;
      padding: 16px;
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      color: white;
      border: none;
      border-radius: 12px;
      font-size: 1.1rem;
      font-weight: 700;
      cursor: pointer;
      transition: transform 0.2s ease, box-shadow 0.2s ease;
      margin-top: 8px;
    }

    .save-btn:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 20px rgba(102, 126, 234, 0.4);
    }

    .save-btn:disabled {
      opacity: 0.6;
      cursor: not-allowed;
      transform: none;
    }

    .info-text {
      text-align: center;
      color: #666;
      font-size: 0.875rem;
      margin-top: 20px;
      padding: 12px;
      background: #f8f9fa;
      border-radius: 10px;
    }

    .vet-filter {
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 12px 16px;
      margin-bottom: 16px;
      background: #f8f9fa;
      border-radius: 10px;
      cursor: pointer;
    }

    .vet-filter input {
      width: 18px;
      height: 18px;
      cursor: pointer;
    }

    .vet-filter span {
      font-size: 0.95rem;
      color: #555;
    }

    .loading {
      text-align: center;
      padding: 60px 20px;
      color: #666;
    }

    @media (max-width: 480px) {
      .settings-container {
        padding: 20px 16px;
        border-radius: 16px;
      }

      .chapters-grid {
        grid-template-columns: repeat(4, 1fr);
        gap: 8px;
      }
      
      .chapter-btn {
        padding: 12px 4px;
        font-size: 0.85rem;
        border-radius: 10px;
      }

      .pages-grid {
        grid-template-columns: repeat(3, 1fr);
        gap: 8px;
      }
      
      .page-btn {
        padding: 10px 4px;
        font-size: 0.75rem;
      }

      .section-title {
        font-size: 1.25rem;
      }
      
      .pages-section {
        padding: 12px;
      }
    }
  `;

  static properties = {
    chapters: { type: Array },
    selectedChapters: { type: Array },
    selectedPages: { type: Object },
    chapterData: { type: Object },
    isLoading: { type: Boolean },
    vetOnly: { type: Boolean },
    verbsOnly: { type: Boolean }
  };

  constructor() {
    super();
    this.chapters = [1, 2, 3, 4, 5, 6, 7, 8];
    this.selectedChapters = [];
    this.selectedPages = {};
    this.chapterData = {};
    this.isLoading = true;
    this.vetOnly = false;
    this.verbsOnly = false;
    this._loadSettings();
    this._loadChapterData();
  }

  _loadSettings() {
    const saved = localStorage.getItem('vocabSettings');
    if (saved) {
      const settings = JSON.parse(saved);
      this.selectedChapters = settings.selectedChapters || [];
      this.selectedPages = settings.selectedPages || {};
      this.vetOnly = settings.vetOnly || false;
      this.verbsOnly = settings.verbsOnly || false;
    }
    // If no chapters selected, default to chapter 1
    if (this.selectedChapters.length === 0) {
      this.selectedChapters = [1];
    }
  }

  async _loadChapterData() {
    for (const chapter of this.chapters) {
      try {
        const url = `./resources/${chapter}.json`;
        console.log('Settings fetching:', url);
        const response = await fetch(url);
        if (response.ok) {
          const data = await response.json();
          this.chapterData[chapter] = data;
        }
      } catch (e) {
        console.error(`Failed to load chapter ${chapter}:`, e);
      }
    }
    
    // If we have a selected chapter but no pages, select all pages by default
    for (const chapter of this.selectedChapters) {
      if (!this.selectedPages[chapter] || this.selectedPages[chapter].length === 0) {
        const pages = this.chapterData[chapter]?.map(p => p.page) || [];
        this.selectedPages = { ...this.selectedPages, [chapter]: pages };
      }
    }
    
    this.isLoading = false;
  }

  _toggleChapter(chapter) {
    if (this.selectedChapters.includes(chapter)) {
      // Deselect if already selected
      this.selectedChapters = [];
      this.selectedPages = {};
    } else {
      // Single selection: replace any previously selected chapter
      this.selectedChapters = [chapter];
      // Select all pages by default for the new chapter
      const pages = this.chapterData[chapter]?.map(p => p.page) || [];
      this.selectedPages = { [chapter]: pages };
    }
    this.requestUpdate();
  }

  _togglePage(chapter, page) {
    const currentPages = this.selectedPages[chapter] || [];
    if (currentPages.includes(page)) {
      this.selectedPages = {
        ...this.selectedPages,
        [chapter]: currentPages.filter(p => p !== page)
      };
    } else {
      this.selectedPages = {
        ...this.selectedPages,
        [chapter]: [...currentPages, page]
      };
    }
  }

  _selectAllPages(chapter) {
    const pages = this.chapterData[chapter]?.map(p => p.page) || [];
    this.selectedPages = { ...this.selectedPages, [chapter]: pages };
  }

  _deselectAllPages(chapter) {
    this.selectedPages = { ...this.selectedPages, [chapter]: [] };
  }

  _saveSettings() {
    const settings = {
      selectedChapters: this.selectedChapters,
      selectedPages: this.selectedPages,
      vetOnly: this.vetOnly,
      verbsOnly: this.verbsOnly
    };
    localStorage.setItem('vocabSettings', JSON.stringify(settings));
    
    this.dispatchEvent(new CustomEvent('settings-saved', {
      detail: settings,
      bubbles: true,
      composed: true
    }));
  }

  render() {
    if (this.isLoading) {
      return html`
        <div class="settings-container">
          <div class="loading">Hoofdstukken laden...</div>
        </div>
      `;
    }

    return html`
      <div class="settings-container">
        <div class="section-title">⚙️ Instellingen</div>

        <div class="settings-section">
          <div class="section-header">
            <span class="section-label">📖 Kies een hoofdstuk</span>
          </div>
          <div class="chapters-grid">
            ${this.chapters.map(ch => html`
              <button
                class="chapter-btn ${this.selectedChapters.includes(ch) ? 'selected' : ''}"
                @click="${() => this._toggleChapter(ch)}"
              >
                Hoofdstuk ${ch}
              </button>
            `)}
          </div>
        </div>

        <label class="vet-filter">
          <input 
            type="checkbox" 
            .checked="${this.vetOnly}"
            @change="${(e) => this.vetOnly = e.target.checked}"
          >
          <span>Alleen vetgedrukte woorden</span>
        </label>

        <label class="vet-filter">
          <input 
            type="checkbox" 
            .checked="${this.verbsOnly}"
            @change="${(e) => this.verbsOnly = e.target.checked}"
          >
          <span>Alleen werkwoorden</span>
        </label>

        ${this.selectedChapters.length > 0 ? html`
          <div class="settings-section">
            <div class="section-header">
              <span class="section-label">📄 Kies pagina's om te oefenen</span>
            </div>
            ${this.selectedChapters.map(ch => {
              // Only show pages for the selected chapter
              const pages = this.chapterData[ch]?.map(p => p.page) || [];
              const selectedPagesInChapter = this.selectedPages[ch] || [];
              return html`
                <div class="pages-section" style="margin-bottom: 12px;">
                  <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px;">
                    <span style="font-weight: 700; color: #667eea; font-size: 0.9rem;">📚 Hoofdstuk ${ch}</span>
                    <button class="select-all-btn" @click="${() => {
                      if (selectedPagesInChapter.length === pages.length) {
                        this._deselectAllPages(ch);
                      } else {
                        this._selectAllPages(ch);
                      }
                    }}">
                      ${selectedPagesInChapter.length === pages.length ? '❌ Deselecteer' : '✓ Selecteer alles'}
                    </button>
                  </div>
                  <div class="pages-grid">
                    ${pages.map(page => html`
                      <button
                        class="page-btn ${selectedPagesInChapter.includes(page) ? 'selected' : ''}"
                        @click="${() => this._togglePage(ch, page)}"
                      >
                        ${page}
                      </button>
                    `)}
                  </div>
                </div>
              `;
            })}
          </div>
        ` : html`
          <div class="no-chapter-selected">
            Selecteer een hoofdstuk om te oefenen
          </div>
        `}

        <button 
          class="save-btn" 
          @click="${this._saveSettings}"
          ?disabled="${this.selectedChapters.length === 0}"
        >
          Instellingen Opslaan
        </button>

        <div class="info-text">
          Hoofdstuk ${this.selectedChapters[0] || '-'} geselecteerd<br>
          ${Object.values(this.selectedPages).flat().length} pagina('s) geselecteerd
        </div>
      </div>
    `;
  }
}

customElements.define('settings-mode', SettingsMode);
